// i18next-extract-mark-ns-start services

import React, { FC, useRef, useState } from 'react'
import SectionHeader from 'src/components/ui/SectionHeader'
import { useTranslation } from 'gatsby-plugin-react-i18next'
import LogoMarquee from 'src/components/ui/LogoMarquee'
import AwardIcon from 'src/components/sections/our-awards/AwardIcon'
import { useOurAwardsAnimations } from 'src/components/sections/our-awards/animations'

export interface IOurAwardsTranslationContent {
  sectionName?: string
  subtitle?: string
  firstPartOfTitle?: string
  secondPartOfTitle?: string
  content?: string
  icons?: Array<{
    icon: string
    title: string
  }>
}

interface IOurAwardsProps {
  className?: string
  translationContent?: IOurAwardsTranslationContent
}

const OurAwards: FC<IOurAwardsProps> = ({ className, translationContent }) => {
  const { t } = useTranslation('services')
  const [isHovered, setIsHovered] = useState(false)

  const subtitleRef = useRef<HTMLDivElement>(null)
  const largeTitleRef = useRef<HTMLDivElement>(null)
  const contentRef = useRef<HTMLDivElement>(null)
  const iconsRef = useRef<HTMLDivElement>(null)

  useOurAwardsAnimations(subtitleRef, largeTitleRef, contentRef, iconsRef)

  const defaultIcons = [
    {
      icon: t('fwaIcon'),
      title: t('fwaTitle'),
    },
    {
      icon: t('effieIcon'),
      title: t('effieTitle'),
    },
    {
      icon: t('webstarIcon'),
      title: t('webstarTitle'),
    },
    {
      icon: t('mobileTrendsIcon'),
      title: t('mobileTrendsTitle'),
    },
    {
      icon: t('golderArrowIcon'),
      title: t('golderArrowTitle'),
    },
    {
      icon: t('KreaturaIcon'),
      title: t('KreaturaTitle'),
    },
  ]

  const subtitle = translationContent?.subtitle || t('OUR AWARDS')
  const firstPartOfTitle = translationContent?.firstPartOfTitle || t('we are proud of')
  const secondPartOfTitle = translationContent?.secondPartOfTitle || t('our achievements')
  const description = translationContent?.content || t('We appreciate')
  const sectionName = translationContent?.sectionName || t('our awards')
  const icons = translationContent?.icons || defaultIcons

  const half = Math.ceil(icons.length / 2)
  const isFew = icons.length <= 6
  const firstRow = isFew ? icons : icons.slice(0, half)
  const secondRow = isFew ? [...icons.slice(half), ...icons.slice(0, half)] : icons.slice(half)

  return (
    <section
      data-name={sectionName}
      id={sectionName}
      className={`${className} w-full py-20 md:py-32 overflow-hidden bg-transparent`}
    >
      <div className="container mb-12 md:mb-20">
        <SectionHeader
          subtitle={subtitle}
          titlePart1={firstPartOfTitle}
          titlePart2={secondPartOfTitle}
          description={description}
          subtitleRef={subtitleRef}
          titleRef={largeTitleRef}
          contentRef={contentRef}
        />
      </div>
      <div
        ref={iconsRef}
        className="w-full space-y-4 md:space-y-8 opacity-0"
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <LogoMarquee baseVelocity={-1} isSlowed={isHovered} slowFactor={0.25}>
          {firstRow.map((icon, idx) => (
            <AwardIcon key={`row1-${icon.title}-${idx}`} icon={icon} className="px-8 md:px-16" />
          ))}
        </LogoMarquee>
        <LogoMarquee baseVelocity={1} isSlowed={isHovered} slowFactor={0.25}>
          {secondRow.map((icon, idx) => (
            <AwardIcon key={`row2-${icon.title}-${idx}`} icon={icon} className="px-8 md:px-16" />
          ))}
        </LogoMarquee>
      </div>
    </section>
  )
}

OurAwards.defaultProps = {
  className: '',
}

export default OurAwards
